import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProspectsPage } from './pages/ProspectsPage';
import { SyndicsPage } from './pages/SyndicsPage';
import { ProspectFormPage } from './pages/ProspectFormPage';
import { ProspectDetailPage } from './pages/ProspectDetailPage';
import { DossierPage } from './pages/DossierPage';
import { ProjetsPage } from './pages/ProjetsPage';
import { EnRetardPage } from './pages/EnRetardPage';
import { LitigesPage } from './pages/LitigesPage';
import { AgendaPage } from './pages/AgendaPage';
import { ParrainagesPage } from './pages/ParrainagesPage';
import { ReportingPage } from './pages/ReportingPage';
import { ParametresPage } from './pages/ParametresPage';
import ImportPage from './pages/ImportPage';
import CorbeillePage from './pages/CorbeillePage';
import { Toaster } from './components/ui/sonner';
import { OfflineBanner } from './components/OfflineBanner';
import './App.css';

const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();
    
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="w-8 h-8 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    if (user) {
        return <Navigate to="/" replace />;
    }
    
    return children;
};

const AppRoutes = () => {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={
                <PublicRoute>
                    <LoginPage />
                </PublicRoute>
            } />
            <Route path="/register" element={<Navigate to="/login" replace />} />

            {/* Protected Routes */}
            <Route path="/" element={
                <ProtectedRoute>
                    <Layout>
                        <DashboardPage />
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/prospects" element={
                <ProtectedRoute>
                    <Layout>
                        <ProspectsPage />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/syndics" element={
                <ProtectedRoute>
                    <Layout>
                        <SyndicsPage />
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/prospects/new" element={
                <ProtectedRoute>
                    <Layout>
                        <ProspectFormPage />
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/prospects/:id" element={
                <ProtectedRoute>
                    <Layout>
                        <ProspectDetailPage />
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/prospects/:id/edit" element={
                <ProtectedRoute>
                    <Layout>
                        <ProspectFormPage />
                    </Layout>
                </ProtectedRoute>
            } />
            
            {/* /pipeline route removed — Kanban drag-and-drop replaced by /projets list view */}
            <Route path="/pipeline" element={<Navigate to="/projets" replace />} />
            
            <Route path="/projets" element={
                <ProtectedRoute>
                    <Layout>
                        <ProjetsPage />
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/en-retard" element={
                <ProtectedRoute>
                    <Layout>
                        <EnRetardPage />
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/litiges" element={
                <ProtectedRoute>
                    <Layout>
                        <LitigesPage />
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/dossiers/:id" element={
                <ProtectedRoute>
                    <Layout>
                        <DossierPage />
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/agenda" element={
                <ProtectedRoute>
                    <Layout>
                        <AgendaPage />
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/parrainages" element={
                <ProtectedRoute>
                    <Layout>
                        <ParrainagesPage />
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/reporting" element={
                <ProtectedRoute>
                    <Layout>
                        <ReportingPage />
                    </Layout>
                </ProtectedRoute>
            } />
            
            <Route path="/parametres" element={
                <ProtectedRoute roles={['admin', 'manager']}>
                    <Layout>
                        <ParametresPage />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/import" element={
                <ProtectedRoute roles={['admin']}>
                    <Layout>
                        <ImportPage />
                    </Layout>
                </ProtectedRoute>
            } />

            <Route path="/corbeille" element={
                <ProtectedRoute roles={['admin']}>
                    <Layout>
                        <CorbeillePage />
                    </Layout>
                </ProtectedRoute>
            } />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <BrowserRouter>
            <ThemeProvider>
                <AuthProvider>
                    <OfflineBanner />
                    <AppRoutes />
                    <Toaster position="top-right" richColors />
                </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
